import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { TruxTableCell } from 'src/components/trux-table-cell';
import { STOP_LABELS, Trip } from '../types';
import { latenessColor, latenessText, tripStatusColor, tripStatusText } from '../trip-utils';
import TrackMap from '../track/track-map';
import { stopLatenessHours, tripDurationHours } from './history-utils';

type TripHistoryDrawerProps = {
  trip: Trip | null;
  driverName: string;
  timezone: string;
  onClose: () => void;
};

export default function TripHistoryDrawer({
  trip,
  driverName,
  timezone,
  onClose,
}: TripHistoryDrawerProps) {
  const formatTime = (value: string | null | undefined) =>
    value ? DateTime.fromISO(value).setZone(timezone).toFormat('LLL d, h:mm a') : '—';

  const durationHours = trip ? tripDurationHours(trip) : null;

  return (
    <Drawer anchor="right" open={!!trip} onClose={onClose}>
      {trip && (
        <Box component="div" sx={{ width: { xs: '100vw', sm: 560 }, p: 2 }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>
              {trip.name || `Trip #${trip.id}`}
            </Typography>
            <Label color={tripStatusColor(trip.status)}>{tripStatusText(trip.status)}</Label>
            <IconButton onClick={onClose}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {driverName}
            {trip.eldVehicleUnitNumber ? ` — Truck #${trip.eldVehicleUnitNumber}` : ''}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Departed {formatTime(trip.startedAt)} · Ended{' '}
            {formatTime(trip.finishedAt || trip.abandonedAt)}
            {durationHours !== null ? ` · ${durationHours.toFixed(1)}h total` : ''}
          </Typography>

          <TrackMap trip={trip} truckPosition={null} />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Planned vs Actual
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TruxTableCell>Stop</TruxTableCell>
                <TruxTableCell>Appointment</TruxTableCell>
                <TruxTableCell>Planned</TruxTableCell>
                <TruxTableCell>Actual</TruxTableCell>
                <TruxTableCell align="right">Delta</TruxTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trip.stops.map((stop, index) => {
                const lateness = stopLatenessHours(stop);
                return (
                  <TableRow key={index}>
                    <TruxTableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                        {STOP_LABELS[stop.label]} — {stop.address}
                      </Typography>
                    </TruxTableCell>
                    <TruxTableCell>{formatTime(stop.appointmentAt)}</TruxTableCell>
                    <TruxTableCell>{formatTime(stop.plannedArrival)}</TruxTableCell>
                    <TruxTableCell>{formatTime(stop.actualArrival)}</TruxTableCell>
                    <TruxTableCell align="right">
                      {lateness !== null ? (
                        <Label color={latenessColor(lateness)}>{latenessText(lateness)}</Label>
                      ) : (
                        '—'
                      )}
                    </TruxTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Drawer>
  );
}
